// <s-card-style>: lets the viewer restyle their credits card (photo + blur, color, sticker, nickname).
// Choices are saved in localStorage, so they stay on this device. Photos are shrunk before saving.
const KEY = 'horhy-card-style';
const PHOTO_KEY = 'horhy-card-photo';
const MAX_SIDE = 900;

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('bad image'));
    img.src = src;
  });
}

// Shrinks the photo so it fits in device storage. If the browser can't redraw it,
// the original is used as long as it is small enough.
async function shrinkPhoto(file, maxSide = MAX_SIDE, quality = 0.8) {
  const original = await readAsDataUrl(file);
  try {
    const img = await loadImage(original);
    const scale = Math.min(1, maxSide / Math.max(img.naturalWidth || 1, img.naturalHeight || 1));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no canvas');
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const out = canvas.toDataURL('image/jpeg', quality);
    if (out.length > 100) return out;
    throw new Error('empty');
  } catch {
    if (original.length < 2_500_000) return original;
    throw new Error('too big');
  }
}

class SCardStyle extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    const $ = (s) => this.querySelector(s);
    const card = $('[data-card]');
    const stickerEl = $('[data-card-sticker]');
    const nameEl = $('[data-card-name]');
    const photoEl = $('[data-card-photo]');
    const shadeEl = $('[data-card-shade]');
    const nameInput = $('[data-name-input]');
    const photoInput = $('[data-photo-input]');
    const photoRemove = $('[data-photo-remove]');
    const photoError = $('[data-photo-error]');
    const blurInput = $('[data-blur-input]');
    const blurValue = $('[data-blur-value]');
    const styleButtons = [...this.querySelectorAll('[data-style]')];
    const stickerButtons = [...this.querySelectorAll('[data-sticker]')];
    const defaultName = this.dataset.defaultName || '';
    if (!card) return;

    const fresh = () => ({ style: 'classic', sticker: '', name: '', blur: 0 });
    let state = fresh();
    let photo = '';
    try {
      state = { ...state, ...JSON.parse(localStorage.getItem(KEY) || '{}') };
      photo = localStorage.getItem(PHOTO_KEY) || '';
    } catch {}

    const save = () => {
      try {
        localStorage.setItem(KEY, JSON.stringify(state));
      } catch {}
    };
    // Returns false when the device has no room left; the photo still shows until the page reloads.
    const savePhoto = () => {
      try {
        if (photo) localStorage.setItem(PHOTO_KEY, photo);
        else localStorage.removeItem(PHOTO_KEY);
        return true;
      } catch {
        return false;
      }
    };

    const render = () => {
      const styleBtn = styleButtons.find((b) => b.dataset.style === state.style) || styleButtons[0];
      if (styleBtn) card.style.background = styleBtn.dataset.bg;
      styleButtons.forEach((b) => b.setAttribute('aria-pressed', String(b === styleBtn)));
      stickerButtons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.sticker === state.sticker)));
      if (stickerEl) stickerEl.textContent = state.sticker;
      if (nameEl) nameEl.textContent = state.name.trim() || defaultName;
      if (nameInput && document.activeElement !== nameInput) nameInput.value = state.name;

      const hasPhoto = Boolean(photo);
      if (photoEl) {
        photoEl.hidden = !hasPhoto;
        if (hasPhoto && photoEl.src !== photo) photoEl.src = photo;
        photoEl.style.filter = `blur(${state.blur}px)`;
      }
      if (shadeEl) shadeEl.hidden = !hasPhoto;
      if (photoRemove) photoRemove.disabled = !hasPhoto;
      if (blurInput) {
        blurInput.disabled = !hasPhoto;
        blurInput.value = String(state.blur);
      }
      if (blurValue) blurValue.textContent = String(state.blur);
    };

    const pop = () => {
      if (!stickerEl || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      stickerEl.animate(
        [{ transform: 'translateY(-50%) scale(0.4) rotate(-20deg)' }, { transform: 'translateY(-50%) scale(1.2) rotate(8deg)' }, { transform: 'translateY(-50%) scale(1)' }],
        { duration: 400, easing: 'ease-out' },
      );
    };

    this.addEventListener('click', (event) => {
      const styleBtn = event.target.closest('[data-style]');
      const stickerBtn = event.target.closest('[data-sticker]');
      if (styleBtn) {
        state.style = styleBtn.dataset.style;
      } else if (stickerBtn) {
        state.sticker = stickerBtn.dataset.sticker;
        render();
        pop();
        save();
        return;
      } else if (event.target.closest('[data-photo-pick]')) {
        photoInput?.click();
        return;
      } else if (event.target.closest('[data-photo-remove]')) {
        photo = '';
        state.blur = 0;
        savePhoto();
      } else if (event.target.closest('[data-reset]')) {
        state = fresh();
        photo = '';
        savePhoto();
        if (nameInput) nameInput.value = '';
      } else {
        return;
      }
      render();
      save();
    }, { signal });

    photoInput?.addEventListener('change', async () => {
      const file = photoInput.files?.[0];
      if (!file) return;
      if (photoError) photoError.hidden = true;
      try {
        photo = await shrinkPhoto(file);
        photoInput.value = '';
        if (!state.blur) state.blur = 4;
        render();
        save();
        if (!savePhoto()) {
          photo = await shrinkPhoto(file, 500, 0.6);
          render();
          savePhoto();
        }
      } catch {
        photoInput.value = '';
        if (photoError) photoError.hidden = false;
      }
    }, { signal });

    blurInput?.addEventListener('input', () => {
      state.blur = Number(blurInput.value) || 0;
      render();
      save();
    }, { signal });

    nameInput?.addEventListener('input', () => {
      state.name = nameInput.value.slice(0, 20);
      render();
      save();
    }, { signal });

    render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }
}

if (!customElements.get('s-card-style')) customElements.define('s-card-style', SCardStyle);
